// Command-center utilization data layer (arc 101 slice 09). Reads the one
// read-only health snapshot `GET /v1/fleet/utilization` — which boxes are busy
// with what class, which keys are hot / cooling and each lane's backlog depth —
// so one page answers them without SSH or psql.
//
// LIVE (`createLiveQuery`, #7612): the body is machine state (`boxes`) plus task
// state (`queue`, `lanes`, each box's `jobs`), so a change on either
// entity's topic RE-READS it — never a patch from the echo. The `machines` topic
// row is this read's `boxes[]` minus the caller-scoped `roles`, which is why the
// read stays the door. Two kinds of field the stream deliberately does NOT
// signal, both covered by {@link UTILIZATION_REFRESH_MS}, a re-read that keeps
// running while live: a box's heartbeat and `stats` (the `machines` version
// ignores them, or every beat re-read every open tab — the storm the live layer
// shipped with and review blocked), and `keys` / `keys_status` / `key_room`,
// which have no topic at all.
// Snake_case mirrors the wire (`apps/api/src/routes/fleet-utilization.ts`).

import {
  type AgentModeKey,
  type BoxStats,
  type BYOKKeyKind,
  type BYOKStatus,
  type HoldRelease,
  MACHINE_JOBS_VERDICT_STATES,
  type MachineHealth,
  type MachineJobState,
  type MachineJobStatus,
  type MachineJobsVerdictState,
  type MachineRoleView,
  type MachineSignals,
  type MachineStatus,
  type PromptRole,
  type RunnerOwnerKind,
  type RunnerRole,
  type TaskClass,
  type TaskLlmPreference,
  type TaskStallKind,
} from '@developerz/domain';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { createLiveQuery } from './live/live-query';
import { orderStamp } from './read-order';
import { SESSION_TOKEN } from './session';

/**
 * A job's epic (#6745): the plan its task is a slice of, as `plan list` counts it.
 * `paused_at` and `llm` (#6753) are the plan's epic pause mark (ISO, null = not paused) and
 * its stored model (null = Automatic). Required on the wire, nullish on read for the
 * bundle-ahead-of-api reason {@link UtilJob} gives: an absent field claims nothing, so an
 * epic without `paused_at` is never shown paused and one without `llm` shows no model.
 */
export type UtilJobEpic = {
  readonly plan_id: string;
  readonly title: string;
  readonly done: number;
  readonly total: number;
  readonly running: number;
  readonly paused_at?: string | null;
  readonly llm?: TaskLlmPreference | null;
};

/** A job's live usage off its machine's beat (#6743): each reading null when not measured. */
export type UtilJobUsage = {
  readonly cpu_pct: number | null;
  readonly mem_bytes: number | null;
  readonly disk_bytes: number | null;
};

/**
 * One job a machine holds for THIS account (#6745, api `fleet-utilization-jobs.ts`). A
 * bundle can roll ahead of its api, so `state` may be one this bundle does not know: the
 * card reads it as no state (`jobState`, `./machine-jobs.ts`), never a crash; `pausable`
 * and `epic` are nullish on read for the same reason.
 */
export type UtilJob = {
  readonly id: string;
  readonly title: string | null;
  readonly repo: string | null;
  readonly run_id: string | null;
  readonly status: MachineJobStatus;
  readonly started_at: string | null;
  readonly class: TaskClass;
  readonly role: PromptRole | null;
  /**
   * The capability group the job moves with (#6769, `AGENT_MODE_KEY_FOR_ORIGIN`): the key a
   * group move names. It differs from `role` for `pr_babysit` (`ci_fixer`, shown under Coder),
   * `kb_fold`, `plan_author` and `setup` (null). Optional on the parse: a server older than
   * #6769 sends none, and an absent key shows no group grip (#6771).
   */
  readonly mode_key?: AgentModeKey | null;
  readonly state: MachineJobState;
  readonly pausable?: boolean;
  readonly usage: UtilJobUsage | null;
  readonly epic?: UtilJobEpic | null;
};

/** One box: durable identity + role merged with its live presence. */
export type UtilBox = {
  readonly id: string;
  /**
   * Friendly box name (#1165) — always a string: the server fills an unnamed box's
   * from its id (#6422), as the roster does, so the card renders it as it stands.
   */
  readonly display_name: string;
  /** Reported hostname, or null before the first beat. */
  readonly hostname: string | null;
  readonly owner_kind: RunnerOwnerKind;
  /** Pinned role — worker | universal | setup | scout | monitor. */
  readonly mode: RunnerRole;
  /**
   * `runners.earmark` (#6760): non-null is a DONATED box. Nullish on read for the
   * `drained_iso` reason; ABSENT reads as unknown, and an unknown box is offered no
   * model-call chip (`capabilityChips`, `./machine-card.ts`).
   */
  readonly earmark?: string | null;
  /**
   * Whether the presence store answered for this read (api `BoxView.presence`). On
   * `'unknown'` the api sends `online: false, active_sessions: 0` — no claim, not a
   * measured idle box. Nullish on read for the `drained_iso` reason: absent reads as `'ok'`.
   */
  readonly presence?: 'ok' | 'unknown';
  readonly online: boolean;
  readonly active_sessions: number;
  /** The scheduling class the box runs now, or null when idle. */
  readonly current_class: TaskClass | null;
  /** Last-beat telemetry (load + free mem/disk), or null. */
  readonly stats: BoxStats | null;
  readonly last_beat_iso: string | null;
  /** The `dz-runner` build this box last reported (arc 101 doc 08, #1039), or null. */
  readonly runner_version: string | null;
  /**
   * When an operator drain held this box out (`runners.drained_at`), null when not.
   * Nullish on read (version skew: a bundle can roll ahead of its api) — absent reads
   * as not drained. The details panel's drain row reads it (`BoxDrainPanel`).
   */
  readonly drained_iso?: string | null;
  /** Enroll-time hardware, or null before enrollment (#6557). */
  readonly specs: { readonly cpu_cores: number; readonly mem_bytes: number } | null;
  /**
   * The physical server the machine runs on (owner ruling G7): `key` groups machines that
   * share a label or, unlabelled, an address; `label` / `address` are what a person reads.
   */
  readonly server: {
    readonly key: string;
    readonly label: string | null;
    readonly address: string | null;
  };
  /** The org login when this is the viewer's private machine from ANOTHER org (G23), else null. */
  readonly via_org: string | null;
  /**
   * The machine's STORED "Jobs at once" cap (`runners.max_slots`, 1..8, #6740): the most
   * jobs it runs at the same time. REQUIRED: the server serves it on every row
   * (`BoxView.max_slots`), and it is the value the details panel and the card menu show,
   * always this read-back and never a click's echo.
   */
  readonly max_slots: number;
  /**
   * When the machine enrolled, null before it did. Nullish on read for the `drained_iso`
   * reason; the details panel's "Connected" line (#6563) reads it.
   */
  readonly enrolled_iso?: string | null;
  /**
   * Who the machine takes work for: the org, or one member by login (`owner.label`). The
   * details panel's "Works for" line (#6563); nullish on read for the `drained_iso` reason.
   */
  readonly owner?: { readonly kind: RunnerOwnerKind; readonly label: string | null };
  /**
   * Every job the machine holds for THIS account (#6745), oldest first: `[]` when idle,
   * running another account's work, or any cross-org row. Nullish on read for the
   * `drained_iso` reason (a bundle can roll ahead of its api): absent reads as no jobs.
   */
  readonly jobs?: readonly UtilJob[] | null;
  /**
   * Whether the machine reports per-job usage (its live beat carries the `jobs` key,
   * #6743). Nullish on read: absent reads as not reporting, so the card never shows
   * "Measuring usage…" for a job its machine will never measure.
   */
  readonly reports_job_usage?: boolean | null;
  /**
   * The machine's roles as the card reads them (#6757, api `BoxRouteView.roles`): each mode
   * key's own switch and whether it is EFFECTIVE here. Null on a row the switches do not
   * govern (another org's or a user-owned machine). Nullish on read for the `drained_iso`
   * reason: absent reads as not reported, and Send work's chips (#6770) then read no role
   * as off.
   */
  readonly roles?: readonly MachineRoleView[] | null;
  /**
   * The machine's move budget (#6768): `left` and `used` in the rolling window, `free_at` (ISO)
   * when the oldest use leaves it, null when none was used. The figure the server's move
   * verdict decides on: the card disables a grip at `left` 0 and computes no limit of its own
   * (#6771). Optional on the parse: absent claims nothing, and the grip stays enabled.
   */
  readonly moves?: {
    readonly left: number;
    readonly used: number;
    readonly free_at: string | null;
  } | null;
  /**
   * The Fleet page's per-machine verdict (`machineVerdict`, `@developerz/domain`, #6557),
   * decided server-side. The page reads its status, health and signals; it never
   * re-derives one from the raw stats. Spelled field by field off the domain's unions,
   * as the rest of this mirror is: the domain publishes the shape as a type only
   * (`machineVerdictWire`), with no schema this app could parse it through.
   */
  readonly verdict: {
    readonly status: MachineStatus;
    readonly health: MachineHealth;
    readonly needs_attention: boolean;
    readonly signals: MachineSignals;
    /**
     * The worst of this account's jobs on the machine (#6746, `machineJobsVerdict`), null when
     * none counts. Nullish on read for the `drained_iso` reason (a bundle can roll ahead of its
     * api), and a member this bundle does not know reads null ({@link normalize}), so a newer
     * api's state never reaches a lookup table here as an unknown key.
     */
    readonly jobs_state?: MachineJobsVerdictState | null;
  };
};

// THE FLEET VERSION SPREAD IS NOT DERIVED HERE. It used to be — a client-side
// fold of these `runner_version` fields, rendered as a chip strip on the Agents
// panel — and it disagreed with `GET /v1/runners/versions`, which is the same
// fact the MCP `box versions` verb answers. This route reads the live presence
// beat ONLY (see `runner_version` above), so every box whose presence key had
// lapsed counted as "unknown" here while the server's fold, which falls back to
// the durable `runners.runner_version` column, named the build it last ran. One
// page, one number, two answers. The spread is the server's alone now
// (`GET /v1/runners/versions`, MCP `box versions`), which is also the one a
// rollout is graded against; the Fleet page no longer renders it (#6559).

/** One pool key: masked metadata merged with its live pool counters. */
export type UtilKey = {
  readonly id: string;
  readonly label: string | null;
  readonly provider: string;
  /** Billing nature — metered | subscription | null (legacy unclassified). */
  readonly kind: BYOKKeyKind | null;
  readonly status: BYOKStatus;
  /** In-flight claims currently signing with this key. */
  readonly in_flight: number;
  /** When this key's cooldown lapses, or null when it is not cooling. */
  readonly cooldown_until_iso: string | null;
};

/** Pool health counts (arc 101 slice 03). */
export type UtilKeysStatus = {
  readonly usable: number;
  readonly cooling: number;
  /** Usable pool non-empty but EVERY usable key is cooling — work waits, pool isn't empty. */
  readonly subs_exhausted: boolean;
};

/** Pending backlog depth for one scheduling class. */
export type UtilLane = { readonly class: TaskClass; readonly depth: number };

/** One reason holding queued work, in the stall report's order (#6708). */
export type UtilQueueReason = {
  readonly kind: TaskStallKind;
  /** The report's count: a `blocked_on_answer` one can include asks no queued task carries. */
  readonly tasks: number;
  /** `key_saturated` only: which parks wait for key room and which for time or a route. */
  readonly by_release?: readonly { readonly releases_on: HoldRelease; readonly tasks: number }[];
};

/**
 * What is really holding the queue (#6708): the api's projection of the stall report.
 * `ready + held` is the lane sum the tile counts. Sent only to a `read:tasks` caller.
 */
export type UtilQueue = {
  readonly ready: number;
  readonly held: number;
  /** The first three reasons, oldest first. */
  readonly top: readonly UtilQueueReason[];
};

/** Key slots standing free beside waiting work, as the worker's alarm persisted it (#6708). */
export type UtilKeyRoom = {
  readonly faulting: boolean;
  readonly keys: readonly { readonly key_id: string; readonly since_iso: string }[];
};

/** The whole `GET /v1/fleet/utilization` body, normalized to a full shape. */
export type FleetUtilization = {
  readonly load: { readonly live_sessions: number; readonly burn: number };
  readonly boxes: readonly UtilBox[];
  readonly keys: readonly UtilKey[];
  readonly keys_status: UtilKeysStatus;
  readonly lanes: readonly UtilLane[];
  /**
   * Nullish on read (#6708): absent for a caller without `read:tasks`, and a bundle can roll
   * ahead of its api. Absent renders today's tile copy.
   */
  readonly queue?: UtilQueue | null;
  /** Nullish on read for the same version-skew reason: absent shows no dispatch row. */
  readonly key_room?: UtilKeyRoom | null;
  /**
   * CLIENT-SIDE, never on the wire (#6753): when the fetch behind this body STARTED, by the
   * browser's clock. The card controls' overrides yield to a read that started after their
   * answer (`./override-bridge.ts`). Absent on a body this client did not fetch: 0, older
   * than every override.
   */
  readonly read_started_ms?: number;
  /**
   * CLIENT-SIDE, never on the wire (#6772): when the same fetch started, in the page's order
   * (`./read-order.ts`); the move budgets compare it with their answers, never by ms.
   */
  readonly read_order?: number;
};

export const FLEET_UTILIZATION_QUERY_KEY = 'fleet_utilization';

/**
 * Fill any missing collection with an empty default so every consumer can read
 * `data.boxes`/`data.keys`/… without a guard. Prod always returns the full
 * shape; a stubbed / partial body (tests) still resolves to a well-formed value.
 */
function normalize(r: Partial<FleetUtilization>): FleetUtilization {
  return {
    load: r.load ?? { live_sessions: 0, burn: 0 },
    boxes: (r.boxes ?? []).map(knownJobsState),
    keys: r.keys ?? [],
    keys_status: r.keys_status ?? { usable: 0, cooling: 0, subs_exhausted: false },
    lanes: r.lanes ?? [],
    queue: r.queue ?? null,
    key_room: r.key_room ?? null,
  };
}

/**
 * A `jobs_state` this bundle knows, else null: absent, null and an unknown member alike. A box
 * with no `verdict` at all (a stubbed or partial body, `normalize`'s own case) passes through
 * as it came: this runs inside the query function, where a throw fails the whole read.
 */
export function knownJobsState(box: UtilBox): UtilBox {
  const verdict: UtilBox['verdict'] | undefined = box.verdict;
  if (verdict === undefined || verdict === null) return box;
  const raw: unknown = verdict.jobs_state;
  const known = MACHINE_JOBS_VERDICT_STATES.find((s) => s === raw) ?? null;
  return { ...box, verdict: { ...verdict, jobs_state: known } };
}

/** The stats and key windows' cadence — this read's own before it went live. */
export const UTILIZATION_REFRESH_MS = 20_000;

/**
 * The account's fleet utilization snapshot, re-read when a machine or a task
 * moves (see the file header). Idle with no session. Keyed by the active org so
 * an org-switch re-reads under the new tenant.
 */
export function useFleetUtilization(token: string | null = SESSION_TOKEN) {
  return createLiveQuery<FleetUtilization>(
    () => ({
      queryKey: [FLEET_UTILIZATION_QUERY_KEY, token, activeAccountId()],
      enabled: token !== null,
      live: [{ entity: 'machine' }, { entity: 'task' }],
      refreshMs: UTILIZATION_REFRESH_MS,
      queryFn: async () => {
        const startedAt = Date.now();
        const order = orderStamp();
        const body = normalize(
          await apiFetch<Partial<FleetUtilization>>('/v1/fleet/utilization', token),
        );
        return { ...body, read_started_ms: startedAt, read_order: order };
      },
    }),
    token,
  ).query;
}
